import { useReducer, useCallback, useEffect } from 'react'

const blacklistedTargets = ['INPUT', 'TEXTAREA']
const modifier = window.navigator.platform.match(/Mac/) ? 'Meta' : 'Control'

const actions = {
  setKeydown: ({ key, override }) => ({
    type: 'setKeydown',
    key,
    override
  }),
  setKeyup: ({ key }) => ({
    type: 'setKeyup',
    key
  }),
  clear: {
    type: 'clear'
  }
}

// exported for testing
export const keysReducer = (state, action) => {
  const { type, key, override } = action
  switch (type) {
    case 'setKeydown':
      if (state === '') {
        return key
      } else if (state === 'm' || state === 't') {
        if (override) return key
        return key === state
          ? state
          : state + key
      } else if (state === modifier || state === `${modifier}Shift`) {
        return (key === 'm' || key === 't')
          ? key
          : state + key
      } else {
        return ''
      }
    case 'setKeyup':
      if (state === 'm' || state === 't') {
        return state
      } else {
        return state.replace(key, '')
      }
    case 'clear':
      return ''
    default:
      return state
  }
}

// the map to store functions should look like
// { 'string': fn } where 'string' is the ordered sequence of keys
// requied to invoke fn
//
// ie for `undo`, the string key should be `${modifier}z`
//
// exported for testing
export const initalKeyMapping = (hotkeys, shortcuts, macros) => {
  const shortcutKeys = new Set()
  const fnMapping = {}
  hotkeys.forEach(k => {
    const { key, fn } = k
    key.split().forEach(seq => {
      shortcutKeys.add(seq)
      fnMapping[seq] = fn
    })
  })

  shortcuts.forEach(shortcut => {
    const { key, fn } = shortcut
    key.split(',').forEach(seq => {
      const split = seq.split('+')
      split.forEach(k => shortcutKeys.add(k))
      fnMapping[split.join('')] = fn
    })
  })

  macros.forEach(macro => {
    const { key, fn } = macro
    key.split().forEach(seq => {
      const split = seq.split(' ')
      split.forEach(k => shortcutKeys.add(k))
      fnMapping[split.join('')] = fn
    })
  })

  return [shortcutKeys, fnMapping]
}

// adapted from https://github.com/arthurtyukayev/use-keyboard-shortcut/blob/master/useKeyboardShortcut.js
export const useKeyboardShortcut = ({ hotkeys, shortcuts, macros }) => {
  const overrideKeys = hotkeys.reduce((acc, hotkey) => {
    acc.push(hotkey.key)
    return acc
  }, [])
  const [shortcutKeys, fnMapping] = initalKeyMapping(hotkeys, shortcuts, macros)

  const [keyState, dispatch] = useReducer(keysReducer, '')

  const keydownListener = useCallback(keydownEvent => {
    const { key, target, repeat } = keydownEvent
    if (blacklistedTargets.includes(target.tagName)) return
    if (shortcutKeys.has(key) && (fnMapping[keyState + key] || fnMapping[keyState])) {
      keydownEvent.preventDefault() // override browser shortcuts (cmd-y is history in chrome)
    }
    if (repeat) return
    if (keyState.includes(key)) return

    dispatch(actions.setKeydown({ key, override: overrideKeys.includes(key) }))
  }, [shortcutKeys, keyState])

  const keyupListener = useCallback(({ key }) => {
    dispatch(actions.setKeyup({ key }))
  }, [shortcutKeys, keyState])

  useEffect(() => {
    window.addEventListener('keydown', keydownListener)
    return () => window.removeEventListener('keydown', keydownListener)
  }, [keydownListener])

  useEffect(() => {
    window.addEventListener('keyup', keyupListener)
    return () => window.removeEventListener('keyup', keyupListener)
  }, [keyupListener])

  useEffect(() => {
    if (fnMapping[keyState]) {
      fnMapping[keyState]()
      dispatch(actions.clear)
    } else if (!Object.keys(fnMapping).some(seq => seq.startsWith(keyState))) {
      // for some reason often the last key in a shortcut sequence will not
      // fire a keyup event when released. this dispatch is definitely a HACK,
      // but it prevents for example `'y'` from being left in the keyState
      dispatch(actions.clear)
    }
  }, [keyState, fnMapping])
}
